/**
 * @tyndo/hooks — wire-shape types for the Predictive module hook layer.
 *
 * The predictive module has no `@tyndo/types` entry (unlike LIMS), so the
 * request/response DTOs the `usePredictive*` hooks are generic over live here,
 * co-located with the hooks and re-exported through the package barrel. Each
 * shape mirrors the backend record/entity it is decoded from (camelCase per the
 * gateway's `PropertyNamingPolicy = CamelCase`); the C# source of truth is noted
 * per group. Keeping them in one file lets the four concern-split hook modules
 * (models / serving / drift / governance) and the consuming predictive-ui views
 * share a single set of shapes with no duplication.
 */

// ────────────────────────────────────────────────────────────────────────────
// Shared list envelope
// ────────────────────────────────────────────────────────────────────────────

/**
 * Some predictive list endpoints return a bare array, others an
 * `{ items | data, __source }` envelope (a provenance-stamped wrapper). The
 * views already tolerate both; `unwrapPredictiveList` collapses either into a
 * plain array so a hook can expose `T[]` while the caller reads `__source` off
 * the raw `.data` when present.
 */
export interface PredictiveListEnvelope<T> {
  __source?: string;
  data?: T[];
  items?: T[];
}

/** Collapse a bare-array-or-envelope list response into a plain array. */
export function unwrapPredictiveList<T>(
  d: T[] | PredictiveListEnvelope<T> | undefined,
): T[] {
  if (!d) return [];
  if (Array.isArray(d)) return d;
  if (Array.isArray(d.items)) return d.items;
  if (Array.isArray(d.data)) return d.data;
  return [];
}

// ────────────────────────────────────────────────────────────────────────────
// Model registry (GET/POST /predictive/models, .../{id}/deploy|retire)
// Mirrors Tyndo.Module.Predictive registry endpoints + PredictiveModel entity.
// ────────────────────────────────────────────────────────────────────────────

export interface PredictiveModel {
  id: string;
  name?: string;
  modelType?: string;
  version?: string;
  status?: string;
  accuracyMetric?: number;
  trainedOn?: string;
  deployedAt?: string;
  retiredAt?: string;
  description?: string;
}

export interface RegisterModelRequest {
  name: string;
  modelType: string;
  version: string;
  onnxBlobPath: string | null;
  inputSchemaJson: string | null;
  outputSchemaJson: string | null;
  accuracyMetric: number;
  trainedOn: string;
  description: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Serving endpoints (GET/POST /predictive/serving/endpoints, .../{id}/*)
// ────────────────────────────────────────────────────────────────────────────

export interface ServingEndpoint {
  id: string;
  name: string;
  route: string;
  modelName?: string;
  modelVersion?: string;
  status: string;
  invocationCount?: number;
  lastInvokedAt?: string | null;
  description?: string | null;
}

export interface RegisterServingEndpointRequest {
  name: string;
  route: string;
  modelId: string;
  description: string | null;
}

/**
 * Per-feature attribution the backend returns alongside a score.
 * `usedRealOnnxRuntime` distinguishes a real, model-grounded attribution
 * (the score came from the ONNX runtime executing the model) from a heuristic
 * fallback (no runtime available — attributions are approximated).
 */
export interface PredictionExplanation {
  usedRealOnnxRuntime?: boolean;
  [k: string]: unknown;
}

/** Result of POST /predictive/serving/endpoints/{id}/invoke. */
export interface ServingInvokeResult {
  score?: number;
  explanation?: PredictionExplanation | null;
  /** Convenience flag mirrored to the top level by some backends. */
  usedRealOnnxRuntime?: boolean;
  [k: string]: unknown;
}

// ────────────────────────────────────────────────────────────────────────────
// Retraining jobs (GET/POST /predictive/retraining, .../{id}/<verb>)
// ────────────────────────────────────────────────────────────────────────────

export interface RetrainingJob {
  id: string;
  predictiveModelId: string;
  trigger: string;
  status: string;
  driftMeasurementId?: string | null;
  requestedAt?: string;
  requestedBy?: string | null;
  approvedBy?: string | null;
  approvalNote?: string | null;
  trainedAccuracy?: number | null;
  resultingArtifactId?: string | null;
  governanceNote?: string | null;
}

export interface RequestRetrainingRequest {
  predictiveModelId: string;
  trigger: string;
  requestedBy: string;
  driftMeasurementId: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Drift + closed-loop (GET /predictive/drift, /predictive/closed-loop/actions)
// ────────────────────────────────────────────────────────────────────────────

export interface DriftReading {
  modelKey: string;
  capturedAt?: string;
  featureDriftKld: number;
  predictionDriftKs: number;
  sampleSize?: number;
  anomalyFlag?: boolean;
}

export interface ClosedLoopAction {
  id: string;
  triggerKind: string;
  triggerReference: string;
  actionKind: string;
  targetReference?: string;
  status: string;
  outcomeJson?: string | null;
  enactedAt?: string | null;
  executedAt?: string | null;
  resultRefId?: string | null;
  createdAt?: string;
}

/** Result of POST /predictive/closed-loop/actions/{id}/enact. */
export interface EnactActionResult {
  success?: boolean;
  status?: string;
  createdId?: string | null;
  failureReason?: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Governance workbench rows (GET /predictive/governance/*)
// Mirrors the feature-store / deployment / gate / scoring / shadow surfaces.
// ────────────────────────────────────────────────────────────────────────────

export interface FeatureDefinitionRow {
  id: string;
  code: string;
  displayName: string;
  sourceKind: string;
  dataType: string;
  status: string;
  isOnlineServed: boolean;
  isOfflineServed: boolean;
  containsPii: boolean;
  ownerUserId?: string | null;
}

export interface FeatureSetRow {
  id: string;
  code: string;
  name: string;
  semanticVersion: string;
  status: string;
  entityKey: string;
  featureCount: number;
  supportsOnlineServing: boolean;
  supportsBatchServing: boolean;
}

export interface ModelDeploymentRow {
  id: string;
  modelArtifactId: string;
  deploymentCode: string;
  environment: string;
  strategy: string;
  status: string;
  trafficShare: number;
  promotedBy?: string | null;
  approvedBy?: string | null;
  promotedAt: string;
  rolledBackAt?: string | null;
  rollbackReason?: string | null;
  latencyP95SloMs?: number | null;
  lastObservedP95LatencyMs?: number | null;
  autoRollbackOnSloBreach?: boolean;
}

export interface ShadowDeploymentRow {
  id: string;
  modelId: string;
  productionVersionId: string;
  shadowVersionId: string;
  status: string;
  agreementRate?: number | null;
  meanDivergence?: number | null;
  latencyRatio?: number | null;
  samplesCompared: number;
  startedAtUtc: string;
  endedAtUtc?: string | null;
}

export interface ModelGovernanceGateRow {
  id: string;
  modelArtifactId: string;
  code: string;
  name: string;
  kind: string;
  verdict: string;
  reviewerRole?: string | null;
  reviewedBy?: string | null;
  requestedAt: string;
  completedAt?: string | null;
  isRequired: boolean;
}

export interface BatchScoringJobRow {
  id: string;
  jobCode: string;
  trigger: string;
  status: string;
  startedAt: string;
  completedAt?: string | null;
  predictionCount: number;
  erroredRowCount: number;
  throughputRps?: number | null;
  durationMs?: number | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Model-governance lifecycle (GET /predictive/governance/model-cards/{id},
//   .../models/{id}/feature-lineage, .../models/{id}/drift-root-cause,
//   .../rollback-evidence/{id})
// Mirrors ModelCard entity, PredictiveFeatureLineage entity,
//   DriftRootCauseReport / DriftFeatureContribution / DriftSuspectedCause,
//   ModelRollbackEvidence entity.
// ────────────────────────────────────────────────────────────────────────────

/** Operator-curated governance card per model (ModelCard : BaseEntity). */
export interface ModelCard {
  id: string;
  modelId: string;
  intendedUse: string;
  trainingDataDescription: string;
  limitationsKnown: string;
  ethicsReview?: string | null;
  fairnessMetrics?: string | null;
  lastReviewedAtUtc?: string | null;
  reviewedByUserId?: string | null;
  approvalStatus: string;
  approvedBy?: string | null;
  approvedByUserId?: string | null;
  approvedAtUtc?: string | null;
  expiresAtUtc?: string | null;
  rejectionReason?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Approval / freshness verdict for a model card (GET
 * /predictive/governance/model-cards/{modelId}/approval). Mirrors the backend
 * `ModelCardApprovalStatus` record: a card is `isServable` only when its
 * required fields are complete, it is `isApproved`, and the approval has not
 * expired. `approvalState` is the raw stored state (Draft / Approved / Rejected
 * / Missing); `isExpired` distinguishes an approved-but-lapsed card from a
 * never-approved one; `reason` is the human verdict.
 */
export interface ModelCardApprovalStatus {
  isServable: boolean;
  requiredFieldsComplete: boolean;
  isApproved: boolean;
  isExpired: boolean;
  approvalState: string;
  expiresAtUtc?: string | null;
  missingFields: string[];
  reason: string;
}

/** One append-only feature-lineage row (PredictiveFeatureLineage : BaseEntity). */
export interface FeatureLineageRow {
  id: string;
  modelId: string;
  featureName: string;
  sourceDatasetId?: string | null;
  sourceColumn?: string | null;
  transformChain?: string | null;
  computedAtUtc: string;
  computedByJobId: string;
  createdAt?: string;
}

/** One ranked per-feature contributor in a drift root-cause report. */
export interface DriftFeatureContribution {
  featureName: string;
  distributionShiftScore: number;
}

/** One suspected cause ranked by heuristic confidence. */
export interface DriftSuspectedCause {
  causeKind: string;
  confidence: number;
  explanation: string;
}

/** Heuristic drift root-cause attribution over a model's readings window. */
export interface DriftRootCauseReport {
  modelId: string;
  windowStartUtc: string;
  windowEndUtc: string;
  topFeatures: DriftFeatureContribution[];
  suspectedCauses: DriftSuspectedCause[];
}

/** One append-only rollback evidence record (ModelRollbackEvidence : BaseEntity). */
export interface RollbackEvidenceRow {
  id: string;
  modelId: string;
  fromVersionId: string;
  toVersionId: string;
  reason: string;
  capturedAtUtc: string;
  affectedPredictionCount: number;
  affectedPredictionRefs?: string | null;
  capturedByUserId?: string | null;
  createdAt?: string;
}
